import { useMemo } from "react";
import { TextField as MuiTextField } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { rv } from "../../tokens";
import { SelectItem } from "../../options";
import FieldShell from "./FieldShell";

interface Props {
  label?: string;
  value: string | undefined;
  onChange: (value: string) => void;
  items: SelectItem[];
  error?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

// Searchable single-select, mirroring KYC's SelectFormAddress (MUI Lab
// Autocomplete). A prefilled value that isn't in the option list is injected
// so saved data always displays and MUI does not warn about an unknown value.
export default function AutocompleteField({
  label,
  value,
  onChange,
  items,
  error,
  required,
  disabled,
  placeholder = "Search or select",
}: Props) {
  const current = value ?? "";
  const selected: SelectItem | null = useMemo(
    () =>
      items.find((item) => String(item.value) === current) ||
      (current !== "" ? { label: current, value: current } : null),
    [current, items]
  );
  const options: SelectItem[] = useMemo(
    () =>
      selected &&
      !items.some((item) => String(item.value) === String(selected.value))
        ? [selected, ...items]
        : items,
    [items, selected]
  );

  return (
    <FieldShell label={label} required={required} error={error}>
      <Autocomplete
        value={selected}
        options={options}
        disabled={disabled}
        fullWidth
        getOptionLabel={(option) => option?.label ?? ""}
        getOptionSelected={(option, chosen) =>
          String(option.value) === String(chosen.value)
        }
        onChange={(_event, option) => onChange(option ? String(option.value) : "")}
        renderInput={(params) => (
          <MuiTextField
            {...params}
            variant="outlined"
            size="small"
            placeholder={placeholder}
            error={Boolean(error)}
            inputProps={{ ...params.inputProps, style: { fontFamily: rv.fontSans } }}
          />
        )}
      />
    </FieldShell>
  );
}
